import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import '@fontsource/jetbrains-mono/700.css';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import { createRoot, type Root } from 'react-dom/client';

import { startupMaintenanceFilter } from '@api/api.queryClient';
import { App } from '@app/main/App';

// MUI X License
LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_LICENSE_KEY ?? '');

if (import.meta.env.MODE === 'production') {
  // ✅ Top-level await
  const { router } = await import('@navigation/router');
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIROMENT,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/\w+\.iidf\.ru\/api/,
      /^https:\/\/\w+\.example\.com\/api/,
    ],
    normalizeDepth: 5,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
    beforeSend: startupMaintenanceFilter.beforeSend,
  });
}

// Components
const AppWithProfiler = Sentry.withProfiler(App);

if (import.meta.env.MODE === 'msw') {
  console.log('process.env.MSW', process.env.MSW_MODE);
  const module = await import('../test_configs/api_mocks/browser');
  const { worker } = module;
  await worker.start({
    onUnhandledRequest(request, print) {
      const globalRegex = /ts|tsx|js/g;
      if (globalRegex.test(request.url)) {
        return;
      }

      // Otherwise, print an unhandled request warning.
      print.warning();
    },
  });
}

// Данные, переживающие переисполнение модуля при HMR
type HotModuleData = {
  root?: Root;
};

// Application rendered
const container = document.querySelector('#root');
if (container) {
  // HMR переисполняет этот модуль прямо в живой странице, поэтому голый createRoot
  // завёл бы второй root на том же контейнере: первое дерево осиротело бы, продолжило
  // дёргать setState и упало на removeChild. Кэш обязан лежать вне модуля — модульная
  // переменная не переживает переисполнение, а import.meta.hot.data переживает.
  // В production сборке import.meta.hot === undefined и вся ветка вырезается.
  const hotModuleData = import.meta.hot?.data as HotModuleData | undefined;
  const root = hotModuleData?.root ?? createRoot(container);

  if (hotModuleData) {
    hotModuleData.root = root;
  }

  root.render(<AppWithProfiler />);
}
